<template>
    <slot name="stone-type"></slot>

    <FilterAccordion
        v-qa="'filter-price'"
        :count="
            priceInCurrency?.min?.amount && priceInCurrency?.max?.amount ? 1 : 0
        "
        :title="t('diamond_search.filter.price.input')"
        hide-count
    >
        <slot name="price"></slot>
    </FilterAccordion>

    <FilterAccordion
        v-qa="'filter-carat-size'"
        :count="weight?.min && weight?.max ? 1 : 0"
        :title="t('diamond_search.filter.carat_size.input')"
        hide-count
    >
        <FilterRangeSegmented
            v-model="weight"
            :segments-config="
                getCaratSegmentsConfig(bounds.weight.min, bounds.weight.max)
            "
            :min-label="'Minimum carat weight'"
            :max-label="'Maximum carat weight'"
            :bounds="caratBounds"
            :value-formatter="formatNumericProperty"
        />
    </FilterAccordion>

    <FilterAccordion
        v-if="cutBounds.length"
        v-qa="'filter-cut'"
        :count="cut?.length || 0"
        :title="t('diamond_search.filter.cut.input')"
    >
        <FilterToggle
            v-model="cut"
            :bounds="cutBounds"
            :disabled="!!(shape && shape !== 'ROUND')"
            :formatter="replace_"
        />
    </FilterAccordion>

    <FilterAccordion
        v-if="colourBounds.length"
        v-qa="'filter-colour'"
        :count="colour?.length || 0"
        :title="t('diamond_search.filter.colour.input')"
    >
        <!-- TODO toggles should be round -->
        <FilterToggle
            v-model="colour"
            :is-multi="!isFancy"
            :bounds="colourBounds"
            :formatter="isFancy ? replaceFANCY_DIAMOND_ : replaceDIAMOND_"
        />
    </FilterAccordion>

    <FilterAccordion
        v-if="clarityBounds.length"
        v-qa="'filter-clarity'"
        :count="clarity?.length || 0"
        :title="t('diamond_search.filter.clarity.input')"
    >
        <FilterToggle
            v-model="clarity"
            :bounds="clarityBounds"
            :formatter="replaceDIAMOND_"
        />
    </FilterAccordion>

    <FilterBase
        v-qa="'checkboxes'"
        class="checkboxes"
    >
        <ThCheckbox
            v-model="hasPhoto"
            v-qa="'photo'"
            :label="t('diamond_search.filter.photo.input')"
            large
        />

        <ThCheckbox
            v-if="GemsApi_StoneCategory.Diamond === category"
            v-model="isCanadaMark"
            v-qa="'canadamark'"
            :label="t('diamond_search.filter.canada_mark.input')"
            large
        />

        <ThCheckbox
            v-model="withOriginCountry"
            v-qa="'provenance'"
            :label="t('diamond_search.filter.origin.input')"
            large
        />
    </FilterBase>

    <FilterAccordion
        v-if="!isFancy && polishBounds.length"
        v-qa="'advanced-filter-button'"
        :count="advancedFiltersCount"
        :title="t('diamond_search.filter.advanced_filters')"
        class="diamond-search-advanced-filters"
        multiple
    >
        <FilterToggle
            v-model="polish"
            v-qa="'filter-polish'"
            :bounds="polishBounds"
            :formatter="replace_"
            :title="t('diamond_search.filter.polish.input')"
        />

        <FilterToggle
            v-model="symmetry"
            v-qa="'filter-symmetry'"
            :bounds="symmetryBounds"
            :formatter="replace_"
            :title="t('diamond_search.filter.symmetry.input')"
        />

        <FilterToggle
            v-model="fluorescence"
            v-qa="'filter-fluorescence'"
            :bounds="fluorescenceBounds"
            :title="t('diamond_search.filter.fluorescence.input')"
        />

        <FilterRange
            v-model="depthPercent"
            v-qa="'filter-depth-percent'"
            :bounds="depthPercentBounds"
            :title="t('diamond_search.filter.depth_percent.input')"
            :min-label="'Minimum depth percent'"
            :max-label="'Maximum depth percent'"
        />

        <FilterRange
            v-model="tablePercent"
            v-qa="'filter-table-percent'"
            :bounds="tablePercentBounds"
            :title="t('diamond_search.filter.table_percent.input')"
            :min-label="'Minimum table percent'"
            :max-label="'Maximum table percent'"
        />

        <FilterRange
            v-model="ratio"
            v-qa="'filter-ratio'"
            :bounds="ratioBounds"
            :step="0.01"
            :title="t('diamond_search.filter.ratio.input')"
            :value-formatter="formatNumericProperty"
            :min-label="'Minimum ratio'"
            :max-label="'Maximum ratio'"
        />

        <div
            v-if="isAdmin"
            v-qa="'consultant-filters'"
            class="consultant-filters"
        >
            <div class="consultant-filters-title">Consultant filters</div>

            <ThCheckbox
                v-model="isDtlReport"
                v-qa="'dtl'"
                :label="t('diamond_search.filter.is_dtl.input')"
                large
            />

            <ThCheckbox
                v-model="isStock"
                v-qa="'stock'"
                :label="t('diamond_search.filter.is_stock.input')"
                large
            />

            <FilterRange
                v-model="length"
                v-qa="'filter-table-length'"
                :bounds="lengthBounds"
                :step="0.01"
                :title="t('diamond_search.filter.length.input')"
                :min-label="'Minimum table length'"
                :max-label="'Maximum table length'"
            />

            <FilterRange
                v-model="width"
                v-qa="'filter-table-width'"
                :bounds="widthBounds"
                :step="0.01"
                :title="t('diamond_search.filter.width.input')"
                :min-label="'Minimum table width'"
                :max-label="'Maximum table width'"
            />

            <FilterRange
                v-model="depth"
                v-qa="'filter-table-depth'"
                :bounds="depthBounds"
                :step="0.01"
                :title="t('diamond_search.filter.depth.input')"
                :min-label="'Minimum table depth'"
                :max-label="'Maximum table depth'"
            />

            <FilterDeliveryDays
                v-model="deliveryDays"
                :bounds="bounds.deliveryDays"
            />

            <FilterToggle
                v-model="shippingFrom"
                v-qa="'filter-shipping-from'"
                :bounds="props.bounds.shippingFromCountry"
                :formatter="(v) => getCountryName(v) || v"
                :title="t('diamond_search.filter.shipping_from.input')"
            />

            <FilterToggle
                v-model="originCountry"
                v-qa="'filter-origin-country'"
                :bounds="props.bounds.originCountry"
                :formatter="(v) => getCountryName(v) || v"
                :title="t('diamond_search.filter.origin_country.input')"
            />
        </div>
    </FilterAccordion>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
    GemsApi_StoneCategory,
    GemsApi_StoneShape,
    type GemsBoundsFragment,
} from '@/graphql/api'

import {
    formatNumericProperty,
    replace_,
    replaceDIAMOND_,
    replaceFANCY_DIAMOND_,
} from '@/domain/stone/labels'
import type { DiamondSearchFilters } from '@/domain/stone/diamond'
import { useBounds, usePercentBounds } from '@/domain/stone'
import { useOrderedCut } from '@/domain/stone/search/useOrderedCut'
import { useOrderedColour } from '@/domain/stone/search/useOrderedColour'
import { useOrderedClarity } from '@/domain/stone/search/useOrderedClarity'
import { useOrderedPolish } from '@/domain/stone/search/useOrderedPolish'
import { useOrderedSymmetry } from '@/domain/stone/search/useOrderedSymmetry'
import { useOrderedFluorescence } from '@/domain/stone/search/useOrderedFluorescence'
import { useFilterBindings } from '@/components/ui/filter/useFilterBindings'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { useTranslator } from '@/i18n'
import { getCaratSegmentsConfig } from '../input-range-config'

import FilterToggle from '@/components/stone/search/sku/filter/FilterToggle.vue'
import FilterRange from '@/components/stone/search/sku/filter/FilterRange.vue'
import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import ThCheckbox from '@/components/ui/inputs/ThCheckbox.vue'
import FilterAccordion from '@/components/ui/filter/FilterAccordion.vue'
import FilterDeliveryDays from '@/components/stone/search/sku/filter/FilterDeliveryDays.vue'
import FilterRangeSegmented from './filter/FilterRangeSegmented.vue'

const props = defineProps<{
    bounds: NonNullable<GemsBoundsFragment>
    values: DiamondSearchFilters
    shape: GemsApi_StoneShape | null | undefined
    category: GemsApi_StoneCategory | null | undefined
    isFancy?: boolean
}>()

const emit = defineEmits<{
    'update:values': [v: DiamondSearchFilters]
}>()

const { t } = useI18n()
const { getCountryName } = useTranslator()

const {
    weight,
    cut,
    colour,
    clarity,
    polish,
    symmetry,
    fluorescence,
    depthPercent,
    tablePercent,
    ratio,
    hasPhoto,
    isCanadaMark,
    withOriginCountry,
    priceInCurrency,
    depth,
    width,
    length,
    deliveryDays,
    isDtlReport,
    isStock,
    originCountry,
    shippingFrom,
} = useFilterBindings(
    computed(() => props.values),
    {},
    emit
)

const isAdmin = useIsAdmin()

const caratBounds = useBounds(() => props.bounds.weight, {
    min: 0.3,
    max: 15,
})

const lengthBounds = useBounds(() => props.bounds.length, {
    min: 0,
    max: 999999,
})

const widthBounds = useBounds(() => props.bounds.width, {
    min: 0,
    max: 999999,
})

const depthBounds = useBounds(() => props.bounds.depth, {
    min: 0,
    max: 999999,
})

const depthPercentBounds = usePercentBounds(() => props.bounds.depthPercent, {
    min: 0,
    max: 100,
})
const tablePercentBounds = usePercentBounds(() => props.bounds.tablePercent, {
    min: 0,
    max: 100,
})
const ratioBounds = useBounds(() => props.bounds.ratio, {
    min: 0.5,
    max: 2.5,
})

const cutBounds = useOrderedCut(() => props.bounds.cut)
const colourBounds = useOrderedColour(() => props.bounds.colour)
const clarityBounds = useOrderedClarity(() => props.bounds.clarity)
const polishBounds = useOrderedPolish(() =>
    props.bounds.polish.filter((polish) =>
        'GOOD' === polish
            ? GemsApi_StoneShape.Rose === props.shape ||
              GemsApi_StoneShape.European === props.shape ||
              GemsApi_StoneShape.Hexagonal === props.shape
            : true
    )
)
const symmetryBounds = useOrderedSymmetry(() => props.bounds.symmetry)
const fluorescenceBounds = useOrderedFluorescence(
    () => props.bounds.fluorescence
)

const advancedFiltersCount = computed(
    () =>
        Number(polish?.value?.length) +
        Number(symmetry?.value?.length) +
        Number(fluorescence?.value?.length) +
        Number(depthPercent?.value?.min && depthPercent.value?.max) +
        Number(tablePercent?.value?.min && tablePercent.value?.max) +
        Number(ratio?.value?.min && ratio.value?.max) +
        Number(length?.value?.min && length.value?.max) +
        Number(width?.value?.min && width.value?.max) +
        Number(depth?.value?.min && depth.value?.max) +
        Number(isDtlReport) +
        Number(isStock) +
        Number(shippingFrom) +
        Number(originCountry)
)
</script>

<style lang="scss" scoped>
.checkboxes {
    margin-top: 20px;
}

.diamond-search-advanced-filters {
    border-top: 1px solid $grey-200;
    margin-bottom: 60px;

    :deep(.content) {
        .title {
            padding: 0;
        }
    }
}

.consultant-filters {
    margin-top: 20px;
    border-top: 1px solid $grey-300;
    padding-top: 20px;

    .consultant-filters-title {
        margin: 0 0 20px;
        font-family: $effra-font-stack;
        font-size: 14px;
        text-transform: uppercase;
        font-weight: 400;
        line-height: 15px;
        letter-spacing: 0.05em;
    }
}
</style>
